import {
    createPreLoader,
    renderComponent,
} from '../common/components/pre-loader';
import {
    getAugmentedRealityPath,
    getPathToScene,
} from '../common/utils/resource';
import {
    QUERY_PARAM__SHOW_ENVIRONMENT,
    SELECTOR__BACKGROUND_CHOOSER,
    SELECTOR__LNK_AUGMENTED_REALITY,
    SELECTOR__BTN_TOGGLE_DIMENSIONS,
    SELECTOR__BTN_RESET_CAMERA,
    SELECTOR__BTN_ROTATE_CAMERA,
    SELECTOR__BTN_TOGGLE_ELECTRICAL_CONNECTION_COVER,
    SELECTOR__BTN_TOGGLE_ILLUMINANT,
    SELECTOR__BTN_TRIGGER_ANIMATIONS,
    SELECTOR__LOADER_CONTAINER,
    SELECTOR_CONTAINER,
    SELECTOR__CONTAINER_PAGE_CONTENT,
    SELECTOR__AR_NOT_SUPPORTED,
    SELECTOR__CONTAINER_QR_AR,
    SELECTOR__AR_SUPPORTED,
    QUERY_PARAM__ALLOW_AR,
    SELECTOR__BTN_TOGGLE_MOUNT,
    SELECTOR__BTN_TOGGLE_OPTICS,
    SELECTOR__BTN_TOGGLE_SENSOR,
} from './constants';
import {
    getObjectByName,
    getObjectsByMatchingName,
    toggleObject,
} from '../common/utils/scene';
import {
    CLASS_NAME__ERROR,
    CSS_CLASS__HIDE,
    NAME__CAMERA_NAME,
    NAME__CAMERA_TARGET,
    QUERY_PARAM__OPEN_AR,
    REGEXP__DIMENSIONS,
    REGEXP__ELECTRICAL_CONNECTION_COVER,
    REGEXP__ILLUMINANT,
    REGEXP__MOUNT,
    REGEXP__OPTICS,
    REGEXP__SENSOR,
    SELECTOR__BUTTON_FULLSCREEN,
} from '../common/constants';
import {
    handleApplicationErrors,
    hasClass,
    toggleClass,
} from '../common/utils/dom';
import {
    ErrorCode,
    InvalidModelError,
    MissingArgumentError,
    ResourceNotFoundError,
} from '../common/error';
import {
    getQueryParam,
    isAndroid,
    showError,
    isSupportedBrowser,
    isLocalResource,
} from '../common/utils';
import { getTheme, getThemeName } from '../common/constants/themes';

// START APP

// Raven is available by direct script-load
if (isSupportedBrowser()) {
    if (window.Raven) {
        window.Raven.context(function () {
            _init();
        });
    } else {
        console.error("Raven couldn't be loaded. Proceeding without");
        _init();
    }
}

// IMPLEMENTATION DETAILS

function _init() {
    if (window.BROWSER_IS_NOT_SUPPORTED) return;

    // Apply theme
    const theme = getTheme();
    document.documentElement.style.setProperty(
        '--primary-color',
        theme.colors.primary
    );
    document.documentElement.style.setProperty(
        '--background-color',
        theme.colors.background
    );
    document.documentElement.style.setProperty(
        '--text-color',
        theme.colors.text
    );

    // Update favicon based on theme
    const themeName = getThemeName().toLowerCase();
    const faviconPath =
        themeName === 'zumtobel'
            ? 'images/zg-favicon.jpg'
            : 'images/favicon-32x32.png';

    document.querySelector('link[rel="icon"]').href = faviconPath;

    const header = document.querySelector('.header');
    if (header) {
        header.setAttribute('data-theme', themeName);
    }

    // Update logo
    const logoElement = document.querySelector('.logo');
    if (logoElement) {
        logoElement.src = theme.logo;
    }

    // Update footer links
    const privacyLink = document.querySelector(
        'footer a[href*="privacy-statement"]'
    );
    const imprintLink = document.querySelector('footer a[href*="imprint"]');
    const termsLink = document.querySelector('footer a[href*="terms-of-use"]');

    if (privacyLink) privacyLink.href = theme.links.privacy;
    if (imprintLink) imprintLink.href = theme.links.imprint;
    if (termsLink) termsLink.href = theme.links.terms;

    const openAR = _hasDirectOpenAR();
    renderComponent(SELECTOR__LOADER_CONTAINER);
    let _prmsPreloader = _prepareContent(openAR);
    // Continue initialization, while model is still loading
    console.log('Registering initialization');
    window.addEventListener('load', async () => {
        const isARAllowed = _isARAllowed();
        _showARMessage(openAR);
        isARAllowed && _prepareAugmentedRealityButton(openAR);
        // Don't continue if the AR view is supposed to be opened directly
        if (openAR) return;
        console.log('Initializing model-viewer');
        try {
            // Wait until the model has loaded
            await _prmsPreloader;
        } catch (e) {
            let pathToScene;
            try {
                pathToScene = getPathToScene(SELECTOR__LOADER_CONTAINER);
            } finally {
                _handleLoadingErrors(e, pathToScene);
            }
        }
        console.log(
            'Pre-loading has completed, proceeding with initializing the controller'
        );

        try {
            const ctrl = (window.modelViewer = await _createController(
                window.apiModelViewer.create
            ));
            _updateView(ctrl);
        } catch (e) {
            handleApplicationErrors(SELECTOR_CONTAINER, e);
        }
    });
}

function _prepareContent(openAR) {
    let prmsPreloader;
    // If the AR view is supposed to be opened directly, the regular model doesn't have to be loaded
    if (openAR) return prmsPreloader;
    // Initialize Pre-loader, but don't wait for the loader to finish yet
    prmsPreloader = new Promise(async (resolve, reject) => {
        console.log('Pre-load scene');
        try {
            await _preloadScene(SELECTOR_CONTAINER, resolve);
        } catch (e) {
            reject(e);
        }
    });
    toggleClass(SELECTOR__CONTAINER_PAGE_CONTENT, CSS_CLASS__HIDE, false);
    return prmsPreloader;
}

function _showARMessage(showAR) {
    if (!showAR) return;
    if (!_isARSupported() && !isAndroid()) {
        toggleClass(SELECTOR__AR_NOT_SUPPORTED, CSS_CLASS__HIDE, false);
    } else {
        toggleClass(SELECTOR__AR_SUPPORTED, CSS_CLASS__HIDE, false);
    }
}

function _hasDirectOpenAR() {
    return getQueryParam(QUERY_PARAM__OPEN_AR) === 'true';
}

function _isARAllowed() {
    return getQueryParam(QUERY_PARAM__ALLOW_AR) === 'true';
}

async function _preloadScene(selectorContainer, handlerComplete) {
    const pathToScene = getPathToScene(selectorContainer);
    // Pre-load scene
    await createPreLoader().addResource(pathToScene).start();
    handlerComplete();
}

async function _createController(createInstance) {
    return createInstance({
        autoRotate: true,
        basePathBackground: BACKGROUND_BASE_PATH, // Injected by webpack
        basePathEnvironment: ENVIRONMENT_MAP_BASE_PATH, // Injected by webpack
        backgroundFileName: '',
        environmentFileName: ENVIRONMENT_MAP_FILE_NAME, // Injected by webpack
        selectorBackgroundChooser: SELECTOR__BACKGROUND_CHOOSER,
        selectorContainer: SELECTOR_CONTAINER,
        selectorResetCamera: SELECTOR__BTN_RESET_CAMERA,
        selectorRotateCamera: SELECTOR__BTN_ROTATE_CAMERA,
        selectorToggleDimensions: SELECTOR__BTN_TOGGLE_DIMENSIONS,
        selectorToggleElectricalConnectionCover:
            SELECTOR__BTN_TOGGLE_ELECTRICAL_CONNECTION_COVER,
        selectorToggleIlluminant: SELECTOR__BTN_TOGGLE_ILLUMINANT,
        selectorToggleMount: SELECTOR__BTN_TOGGLE_MOUNT,
        selectorToggleOptics: SELECTOR__BTN_TOGGLE_OPTICS,
        selectorToggleSensor: SELECTOR__BTN_TOGGLE_SENSOR,
    });
}

function _updateView(ctrl) {
    const scene = ctrl.modelViewer.scene;
    const params = new URLSearchParams(window.location.search);
    if (params.get(QUERY_PARAM__SHOW_ENVIRONMENT) === 'true') {
        const elmtContBackground = document.querySelector('.cont-background');
        elmtContBackground && elmtContBackground.classList.remove('hide');
    }
    if (_supportsFullscreen()) {
        document
            .querySelector(SELECTOR__BUTTON_FULLSCREEN)
            .classList.remove(CSS_CLASS__HIDE);
    }
    _initVisibility(scene);
    _showButtons(ctrl.modelViewer);
}

function _initVisibility(scene) {
    toggleObject(REGEXP__DIMENSIONS, scene, {
        visible: false,
    });
}

function _showButtons(modelViewer) {
    const scene = modelViewer.scene;
    _showElementByModelObject(
        scene,
        SELECTOR__BTN_TOGGLE_DIMENSIONS,
        REGEXP__DIMENSIONS
    );
    _showElementByModelObject(
        scene,
        SELECTOR__BTN_TOGGLE_ELECTRICAL_CONNECTION_COVER,
        REGEXP__ELECTRICAL_CONNECTION_COVER
    );
    _showElementByModelObject(scene, SELECTOR__BTN_TOGGLE_MOUNT, REGEXP__MOUNT);
    _showElementByModelObject(
        scene,
        SELECTOR__BTN_TOGGLE_OPTICS,
        REGEXP__OPTICS
    );
    _showElementByModelObject(
        scene,
        SELECTOR__BTN_TOGGLE_SENSOR,
        REGEXP__SENSOR
    );
    _showElementByModelObject(
        scene,
        SELECTOR__BTN_TOGGLE_ILLUMINANT,
        REGEXP__ILLUMINANT
    );

    const animationController = modelViewer.animationController;
    toggleClass(
        SELECTOR__BTN_TRIGGER_ANIMATIONS,
        CSS_CLASS__HIDE,
        !animationController
    );
    document
        .querySelector(SELECTOR__BTN_TRIGGER_ANIMATIONS)
        .addEventListener('click', () => animationController.startAnimation());
}

function _showElementByModelObject(scene, selectorElement, name) {
    const found =
        name instanceof RegExp
            ? !!getObjectsByMatchingName(scene, name).length
            : getObjectByName(scene, name);
    toggleClass(selectorElement, CSS_CLASS__HIDE, !found);
}

function _prepareAugmentedRealityButton(openAR) {
    const elmtLnkAR = document.querySelector(SELECTOR__LNK_AUGMENTED_REALITY);
    elmtLnkAR.classList.remove(CSS_CLASS__HIDE);
    if (isAndroid()) {
        _addARAndroidHandler(elmtLnkAR);
    } else if (_isARSupported()) {
        // iOS
        _setARPath(elmtLnkAR);
    } else {
        _addQRRendererHandler(elmtLnkAR);
        return;
    }
    if (openAR) {
        console.log('Opening AR-view');
        _clickElement(elmtLnkAR);
    }
}

function _setARPath(element) {
    element.rel = 'ar';
    element.href = getAugmentedRealityPath(
        SELECTOR_CONTAINER,
        // Injected by webpack
        AUGMENTED_REALITY_BASE_PATH
    );
}

function _addQRRendererHandler(element) {
    element.addEventListener('click', _toggleArQrCode);
}

function _addARAndroidHandler(elmtLnkAR) {
    // Set link with "Your device does not support AR"-message (maybe with History-API?)
    const fallbackUrl = 'https://developers.google.com/ar';
    let urlModel = getPathToScene(SELECTOR_CONTAINER);
    if (!urlModel.startsWith('http')) {
        urlModel = `${window.location.origin}${
            !urlModel.startsWith('/') ? '/' : ''
        }${urlModel}`;
    }
    elmtLnkAR.href =
        `intent://arvr.google.com/scene-viewer/1.0?file=${urlModel}&mode=ar_preferred#Intent;` +
        `scheme=${new URL(urlModel).protocol.split(':').shift()};` +
        `package=com.google.android.googlequicksearchbox;action=android.intent.action.VIEW;` +
        `S.browser_fallback_url=${fallbackUrl};end;`;
}

function _isARSupported() {
    const a = document.createElement('a');
    return a.relList.supports('ar');
}

function _clickElement(element) {
    element.click();
}

async function _toggleArQrCode() {
    toggleClass(SELECTOR__CONTAINER_QR_AR, CSS_CLASS__HIDE);
    if (hasClass(SELECTOR__CONTAINER_QR_AR, CSS_CLASS__HIDE)) {
        return;
    }
    if (document.querySelector(`${SELECTOR__CONTAINER_QR_AR} svg`)) return;
    const elmtQRSvg = window.apiModelViewer.createQRCode(_createDirectARUrl(), {
        // Using a value of 0, will make the library use its default value
        margin: 0.00001,
    });
    document.querySelector(SELECTOR__CONTAINER_QR_AR).appendChild(elmtQRSvg);
}

function _createDirectARUrl() {
    const location = window.location;
    const query = new URLSearchParams(location.search);
    query.set(QUERY_PARAM__OPEN_AR, 'true');
    return `${location.origin}${location.pathname}?${query.toString()}`;
}

function _supportsFullscreen() {
    return !![
        document.body.requestFullscreen,
        document.body.webkitRequestFullscreen,
        document.body.mozRequestFullscreen,
    ].find((handler) => handler);
}

function _handleLoadingErrors(error, pathToScene) {
    // Don't report errors, related to resources from localhost
    const isLocal = isLocalResource(pathToScene);
    toggleClass(SELECTOR_CONTAINER, CLASS_NAME__ERROR, true);
    if (error instanceof ResourceNotFoundError) {
        console.error('Model could not be found');
        showError('Model could not be found');
        if (!isLocal) throw error;
    }
    if (error instanceof MissingArgumentError) {
        if (error.message === ErrorCode.missingScenePath) {
            showError('No "scene" value found');
            // Very likely has been called manually, thus no error has to be thrown
            // (an in order to that, so Sentry-entry)
            return;
        }
        showError();
        if (!isLocal) throw error;
    }
    console.log('An error occurred during loading the model');
    if (!isLocal) throw error;
}
